import { Injectable } from '@angular/core';
import Clarity from '@microsoft/clarity';

export interface ClarityProfileTags {
  name?: string;
  email?: string;
  vesselName?: string;
  distanceUnit?: 'nautical' | 'meters' | 'kilometers' | string;
  speedUnit?: 'knots' | 'mph' | 'kmh' | string;
  gpsType?: 'gps' | 'loran' | string;
  friendsCount?: number | string;
  communitiesCount?: number | string;
  activitiesCount?: number | string;
}

@Injectable({ providedIn: 'root' })
export class ClarityService {

  /**
   * Web SDK equivalent to setCustomUserId.
   * Call when the user logs in or their profile is loaded.
   */
  identify(userId: string): void {
    try {
      Clarity.identify(userId);
    } catch (e) {
      // Clarity not ready yet — safe to ignore
    }
  }

  // Backwards-compatible wrapper (existing call sites use this signature).
  identifyUser(userId: string, email?: string, name?: string): void {
    try {
      Clarity.identify(userId, undefined, undefined, email ?? name ?? userId);
    } catch (e) {
      // Clarity not ready yet — safe to ignore
    }
  }

  /**
   * Web SDK equivalent to setCustomTag.
   */
  setTag(key: string, value: string): void {
    try {
      Clarity.setTag(key, value);
    } catch (e) {
      // ignore
    }
  }

  setTags(tags: Record<string, string | undefined | null>): void {
    Object.entries(tags).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        this.setTag(key, String(value));
      }
    });
  }

  /**
   * Sets session-level user attribute tags. Call whenever the user profile
   * is loaded, updated, or settings are changed.
   */
  setUserProfileTags(profile: ClarityProfileTags): void {
    this.setTags({
      name: profile.name,
      email: profile.email,
      vesselName: profile.vesselName,
      distanceUnit: profile.distanceUnit,
      speedUnit: profile.speedUnit,
      gpsType: profile.gpsType,
      friendsCount: profile.friendsCount !== undefined ? String(profile.friendsCount) : undefined,
      communitiesCount: profile.communitiesCount !== undefined ? String(profile.communitiesCount) : undefined,
      activitiesCount: profile.activitiesCount !== undefined ? String(profile.activitiesCount) : undefined,
    });
  }

  setTripState(active: boolean, status?: 'active' | 'paused' | 'ended'): void {
    this.setTag('trip_active', active ? 'true' : 'false');
    if (status) {
      this.setTag('trip_status', status);
    }
  }

  setLastWaypointCreated(isoTimestamp: string = new Date().toISOString()): void {
    this.setTag('last_waypoint_created', isoTimestamp);
  }

  /**
   * Milestone action tags — set to "true" once a user completes the
   * milestone in a session (action_login, action_signup, action_start_trip,
   * action_end_trip, action_create_waypoint, action_create_vessel,
   * action_delete_vessel, ...).
   */
  setAction(actionKey: string): void {
    this.setTag(actionKey, 'true');
  }

  /**
   * Web SDK equivalent to sendCustomEvent.
   */
  trackEvent(eventName: string): void {
    try {
      Clarity.event(eventName);
    } catch (e) {
      // ignore
    }
  }
}
